import React, {useState, useCallback, useEffect, useMemo} from 'react';
import {FlexColumn, FlexRow, Modal, TextField} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import RichTextItem from '../richtext_item';
import CreateDeliverableMutation from '../../mutations/deliverables/CreateDeliverableMutation';
import DeleteDeliverableMutation from '../../mutations/deliverables/DeleteDeliverableMutation';
import UpdateDeliverableMutation from '../../mutations/deliverables/UpdateDeliverableMutation';
import {CommitMutation} from '../../mutations/CommitMutation';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util from '../../forecast-app/shared/util/util';
import {Deliverable, DeliverableEdges, ExpenseRow, RoleRow} from './BaselineTypes';

type DeliverableEditorModalProps = {
	closeModal: () => void;
	deliverables: DeliverableEdges;
	deliverable?: Deliverable;
	action: 'create' | 'edit';
	projectId: string;
	onSuccess?: (deliverable: Deliverable) => void;
	baselineItems: (RoleRow | ExpenseRow)[];
};

const DeliverableEditorModal = ({
	closeModal,
	deliverables,
	deliverable,
	action,
	projectId,
	onSuccess,
	baselineItems,
}: DeliverableEditorModalProps): JSX.Element => {
	const intl = useIntl();

	const [description, setDescription] = useState<string>(deliverable?.description || '');
	const [name, setName] = useState<string>(deliverable?.name || '');
	const [closeEditor, setCloseEditor] = useState(false);

	const isCreate = action === 'create';
	const modalTitle = isCreate
		? intl.formatMessage({id: 'deliverables.edit.modal.add_new_deliverable'})
		: intl.formatMessage({id: 'deliverables.edit.modal.edit_deliverable'});
	const buttonText = isCreate
		? intl.formatMessage({id: 'deliverables.edit.modal.add_deliverable'})
		: intl.formatMessage({id: 'deliverables.edit.modal.save_changes'});

	/* Edit Deliverable */

	const onUpdateSuccess = response => {
		closeModal();
		const updatedDeliverable = response?.updateDeliverable?.deliverable?.node;
		const updatedDeliverables = deliverables.map(deliverable =>
			deliverable?.node?.id === updatedDeliverable.id ? {node: updatedDeliverable} : deliverable
		);
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: updatedDeliverables,
			projectId,
			baselineItems,
		});
	};

	const handleSave = useCallback(() => {
		deliverable?.id &&
			CommitMutation(
				UpdateDeliverableMutation,
				{id: String(deliverable.id), name, description, projectId},
				onUpdateSuccess
			);
	}, [name, description, deliverables, projectId, closeModal]);

	/* Create Deliverable */

	const onCreateSuccess = response => {
		closeModal();
		const deliverable = response?.createDeliverable?.deliverable?.node;
		if (onSuccess && deliverable) {
			return onSuccess(deliverable);
		}
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: [...deliverables, {node: deliverable}],
			projectId,
			baselineItems,
		});
	};

	const handleCreate = useCallback(() => {
		CommitMutation(CreateDeliverableMutation, {name, description, projectId}, onCreateSuccess);
	}, [name, description, deliverables, projectId, closeModal]);

	/* Delete Deliverable */

	const deliverablesWithEstimates = useMemo(
		() => new Set(baselineItems ? baselineItems.map(item => item.deliverableId) : []),
		[baselineItems]
	);

	const onDeleteSuccess = response => {
		setCloseEditor(true);
		closeModal();
		const deletedDeliverableId = response?.deleteDeliverable?.deletedDeliverableId;
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: deliverables.filter(deliverable => deliverable?.node?.id !== deletedDeliverableId),
			projectId,
			baselineItems,
		});
	};

	const handleDelete = () => {
		deliverable?.id && Util.CommitMutation(DeleteDeliverableMutation, {id: deliverable.id, projectId}, onDeleteSuccess);
	};

	const deleteConfirmation = () => {
		showModal({
			type: MODAL_TYPE.DELETE_DELIVERABLE_CONFIRMATION_MODAL,
			handleDelete,
			hasEstimates: deliverable?.id && deliverablesWithEstimates.has(deliverable.id),
		});
	};

	useEffect(() => {
		if (closeEditor) {
			closeModal();
		}
	}, [closeEditor]);

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal}>
			<Modal.Title>{modalTitle}</Modal.Title>
			<Modal.Content>
				<TextField
					label={intl.formatMessage({id: 'deliverables.manage.modal.deliverable_name'})}
					value={name}
					onValueChange={setName}
					size="large"
					width="flex"
				/>
				<FlexColumn>
					<FlexRow>{intl.formatMessage({id: 'deliverables.edit.modal.description_optional'})}</FlexRow>
					<RichTextItem
						cy="deliverable-description"
						alwaysShowControls={true}
						text={description}
						handleTextChange={setDescription}
						hideEmojiPicker
					/>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					disabled={name === ''}
					onPress={isCreate ? handleCreate : handleSave}
					dataCy="action"
				>
					<FormattedMessage id={buttonText} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
				{!isCreate && (
					<Modal.TertiaryFooterButton onPress={deleteConfirmation}>
						{intl.formatMessage({id: 'deliverables.edit.modal.delete_deliverable'})}{' '}
					</Modal.TertiaryFooterButton>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default DeliverableEditorModal;
